<script lang="ts">
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import Table from '../../components/Table.svelte'
  import Modal from '../../components/Modal.svelte'
  import FormField from '../../components/FormField.svelte'
  import { showApiError, showError } from '$lib/util/errors'

  type OccupancyWithRefs =
    paths['/occupancies']['get']['responses'][200]['content']['application/json']['items'][number]
  type OccupancyStatus = 'ACTIVE' | 'PENDING' | 'ENDED' | 'CANCELLED'

  let items = $state<OccupancyWithRefs[]>([])
  let nextCursor = $state<number | undefined>(undefined)
  let statusFilter = $state<OccupancyStatus | ''>('')
  let loading = $state(false)
  let showModal = $state(false)
  let editing = $state<OccupancyWithRefs | null>(null)

  let personId = $state<number | null>(null)
  let animalId = $state<number | null>(null)
  let unitId = $state<number | null>(null)
  let leaseId = $state<number | null>(null)
  let startDate = $state('')
  let endDate = $state('')
  let status = $state<OccupancyStatus>('ACTIVE')

  let personOpts = $state<{ id: number; label: string }[]>([])
  let animalOpts = $state<{ id: number; label: string }[]>([])
  let unitOpts = $state<{ id: number; label: string }[]>([])

  interface FlatOccupancy {
    id: number
    person: string
    animal: string
    unit: string
    startDate: string
    status: string
  }

  const rows: () => FlatOccupancy[] = () =>
    items.map((o) => ({
      id: o.id,
      person: `${o.person.firstName} ${o.person.lastName}`,
      animal: o.animal.name,
      unit: o.unit.streetAddress,
      startDate: o.startDate,
      status: o.status,
    }))

  async function loadOptions(): Promise<void> {
    const [p, a, u] = await Promise.all([
      api.GET('/people', { params: { query: { limit: 50 } } }),
      api.GET('/animals', { params: { query: { limit: 50 } } }),
      api.GET('/units', { params: { query: { limit: 50 } } }),
    ])
    personOpts = (p.data?.items ?? []).map((it) => ({
      id: it.id,
      label: `${it.firstName} ${it.lastName}`,
    }))
    animalOpts = (a.data?.items ?? []).map((it) => ({ id: it.id, label: it.name }))
    unitOpts = (u.data?.items ?? []).map((it) => ({ id: it.id, label: it.streetAddress }))
  }

  async function load(cursor?: number): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/occupancies', {
      params: {
        query: {
          limit: 50,
          ...(cursor !== undefined ? { cursor } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
        },
      },
    })
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = cursor !== undefined ? [...items, ...(data?.items ?? [])] : (data?.items ?? [])
    nextCursor = data?.nextCursor
    loading = false
  }

  function openCreate(): void {
    editing = null
    personId = null
    animalId = null
    unitId = null
    leaseId = null
    startDate = ''
    endDate = ''
    status = 'ACTIVE'
    void loadOptions()
    showModal = true
  }

  function openEdit(row: FlatOccupancy): void {
    const found = items.find((o) => o.id === row.id)
    if (!found) return
    editing = found
    personId = found.person.id
    animalId = found.animal.id
    unitId = found.unit.id
    leaseId = found.lease?.id ?? null
    startDate = found.startDate
    endDate = found.endDate ?? ''
    status = found.status
    void loadOptions()
    showModal = true
  }

  async function handleSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    if (personId === null || animalId === null || unitId === null || !startDate) {
      showError({ detail: 'Person, animal, unit, and start date are required.' })
      return
    }
    const body = {
      personId,
      animalId,
      unitId,
      ...(leaseId !== null ? { leaseId } : {}),
      startDate,
      ...(endDate ? { endDate } : {}),
      status,
    }
    if (editing) {
      const { data, error } = await api.PUT('/occupancies/{id}', {
        params: { path: { id: editing.id } },
        body,
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = items.map((o) => (o.id === editing?.id ? data : o))
    } else {
      const { data, error } = await api.POST('/occupancies', { body })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = [data, ...items]
    }
    showModal = false
  }

  async function handleDelete(id: number): Promise<void> {
    if (!confirm('Delete this occupancy?')) return
    const { error } = await api.DELETE('/occupancies/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    items = items.filter((o) => o.id !== id)
  }

  $effect(() => {
    void statusFilter
    void load()
  })
</script>

<div class="section-header">
  <h1>Occupancies</h1>
  <div class="section-actions">
    <label>
      Status:
      <select bind:value={statusFilter}>
        <option value="">All</option>
        <option value="ACTIVE">Active</option>
        <option value="PENDING">Pending</option>
        <option value="ENDED">Ended</option>
        <option value="CANCELLED">Cancelled</option>
      </select>
    </label>
    <button type="button" class="primary" onclick={openCreate}>+ Add occupancy</button>
  </div>
</div>

<Table
  items={rows()}
  {loading}
  columns={[
    { key: 'person', label: 'Person' },
    { key: 'animal', label: 'Animal' },
    { key: 'unit', label: 'Unit' },
    { key: 'startDate', label: 'Start date' },
    { key: 'status', label: 'Status' },
  ]}
  onEdit={openEdit}
  onDelete={handleDelete}
/>

{#if nextCursor !== undefined}
  <button type="button" class="load-more" onclick={() => void load(nextCursor)} disabled={loading}>
    {loading ? 'Loading…' : 'Load more'}
  </button>
{/if}

{#if showModal}
  <Modal title={editing ? 'Edit occupancy' : 'Add occupancy'} onclose={() => (showModal = false)}>
    <form onsubmit={handleSubmit}>
      <div class="form-field">
        <label for="person">Person *</label>
        <select id="person" bind:value={personId} required>
          <option value={null}>- Select -</option>
          {#each personOpts as opt (opt.id)}
            <option value={opt.id}>{opt.label}</option>
          {/each}
        </select>
      </div>
      <div class="form-field">
        <label for="animal">Animal *</label>
        <select id="animal" bind:value={animalId} required>
          <option value={null}>- Select -</option>
          {#each animalOpts as opt (opt.id)}
            <option value={opt.id}>{opt.label}</option>
          {/each}
        </select>
      </div>
      <div class="form-field">
        <label for="unit">Unit *</label>
        <select id="unit" bind:value={unitId} required>
          <option value={null}>- Select -</option>
          {#each unitOpts as opt (opt.id)}
            <option value={opt.id}>{opt.label}</option>
          {/each}
        </select>
      </div>
      <FormField label="Start date" name="startDate" type="date" bind:value={startDate} required />
      <FormField label="End date" name="endDate" type="date" bind:value={endDate} />
      <div class="form-field">
        <label for="status">Status</label>
        <select id="status" bind:value={status}>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending</option>
          <option value="ENDED">Ended</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>
      <button type="submit" class="primary">Save</button>
    </form>
  </Modal>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .section-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  .section-actions label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
  }
  .section-actions select {
    padding: 4px 8px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
  button.load-more {
    padding: 8px 16px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .form-field select {
    padding: 6px 10px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 0.875rem;
  }
</style>
